import type { FormFieldMeta } from "@/shared/lib/forms";

/**
 * Field types whose form value is an object (or null), never a string. Their
 * generated validation schema rejects `''`, so an empty one has to open on
 * `null` or it fails with "expected object, received string" and blocks the
 * whole form - a field nobody filled in stops the fields they did from saving.
 */
export const OBJECT_VALUED_FIELD_TYPES = new Set<string>([
  "ENTITY_SEARCH",
  "ADDRESS",
]);

/** Code names among `fields` whose value is object-shaped. */
export function objectValuedCodeNames(fields: FormFieldMeta[]): Set<string> {
  return new Set(
    fields
      .filter((f) => OBJECT_VALUED_FIELD_TYPES.has(f.field_type ?? ""))
      .map((f) => f.code_name),
  );
}

/**
 * Whether a value carries no information.
 *
 * Recursive because the shapes differ: a picker stores `{uuid, name}`, an
 * address stores component keys, and `AddressInput` emits all-blank strings
 * when opened and cleared. `0` and `false` are values, not blanks.
 */
export function isBlankValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object")
    return Object.values(value as Record<string, unknown>).every(isBlankValue);
  return false;
}

/**
 * Seed an edit form so an empty object-valued field opens on `null`.
 *
 * Covers a field that was never set AND one a previous save cleared to `''`,
 * which is the empty the backend stores. Without the second case, clearing a
 * picker once would make every later edit of that card fail validation.
 */
export function seedObjectValuedValues(
  fields: FormFieldMeta[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const objectValued = objectValuedCodeNames(fields);
  if (objectValued.size === 0) return values;
  const out: Record<string, unknown> = { ...values };
  for (const code of objectValued) {
    if (isBlankValue(out[code])) out[code] = null;
  }
  return out;
}

/**
 * Reduce a submitted payload so a blank object-valued field says what the user
 * meant, distinguishing the two cases the form itself cannot tell apart.
 *
 * - Never set (blank before, blank now): omit the key. Submitting it would
 *   write an empty row for every untouched optional picker on the card.
 * - Cleared (had a value, blank now): send `''`. The picker renders a Clear
 *   button, so dropping the key here would make that button silently no-op -
 *   the save succeeds and the old value comes back on the next refetch.
 */
export function normaliseObjectValuedSubmission(
  fields: FormFieldMeta[],
  originalValues: Record<string, unknown>,
  submitted: Record<string, unknown>,
): Record<string, unknown> {
  const objectValued = objectValuedCodeNames(fields);
  if (objectValued.size === 0) return submitted;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(submitted)) {
    if (!objectValued.has(key) || !isBlankValue(value)) {
      out[key] = value;
      continue;
    }
    if (isBlankValue(originalValues[key])) continue;
    out[key] = "";
  }
  return out;
}
